import { useState, useCallback, useEffect, useRef } from 'react';
import type { Position, SnapZone } from '../types';
import { TASKBAR_HEIGHT } from '../utils/constants';

// Snap zone detection threshold in pixels
const SNAP_THRESHOLD = 20;

interface UseDraggableOptions {
  initialPosition: Position;
  onDragStart?: () => void;
  onDrag?: (position: Position, snapZone: SnapZone) => void;
  onDragEnd?: (position: Position, snapZone: SnapZone) => void;
  disabled?: boolean;
  bounds?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
}

interface UseDraggableReturn {
  position: Position;
  isDragging: boolean;
  snapZone: SnapZone;
  dragHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
  };
  setPosition: (pos: Position) => void;
}

// Detect which snap zone the cursor is in based on screen position
const detectSnapZone = (clientX: number, clientY: number): SnapZone => {
  const screenWidth = window.innerWidth;

  // Top edge - maximize
  if (clientY <= SNAP_THRESHOLD) {
    return 'maximize';
  }

  // Left edge - left half
  if (clientX <= SNAP_THRESHOLD) {
    return 'left';
  }

  // Right edge - right half
  if (clientX >= screenWidth - SNAP_THRESHOLD) {
    return 'right';
  }

  return null;
}

export const useDraggable = ({
  initialPosition,
  onDragStart,
  onDrag,
  onDragEnd,
  disabled = false,
  bounds,
}: UseDraggableOptions): UseDraggableReturn => {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [snapZone, setSnapZone] = useState<SnapZone>(null);
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const currentSnapZoneRef = useRef<SnapZone>(null);

  // Update position when initialPosition changes externally
  useEffect(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  const constrainPosition = useCallback(
    (x: number, y: number): Position => {
      const minX = bounds?.left ?? 0;
      const minY = bounds?.top ?? 0;
      const maxX = bounds?.right ?? window.innerWidth - 100;
      const maxY = bounds?.bottom ?? window.innerHeight - TASKBAR_HEIGHT - 30;

      return {
        x: Math.max(minX, Math.min(x, maxX)),
        y: Math.max(minY, Math.min(y, maxY)),
      };
    },
    [bounds]
  );

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragStartRef.current) return;

      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;

      const newPosition = constrainPosition(
        dragStartRef.current.posX + deltaX,
        dragStartRef.current.posY + deltaY
      );

      // Detect snap zone based on cursor position
      const detectedZone = detectSnapZone(clientX, clientY);
      currentSnapZoneRef.current = detectedZone;
      setSnapZone(detectedZone);

      setPosition(newPosition);
      onDrag?.(newPosition, detectedZone);
    },
    [constrainPosition, onDrag]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    },
    [handleMove]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 1) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    [handleMove]
  );

  const handleEnd = useCallback(() => {
    if (dragStartRef.current) {
      setIsDragging(false);
      document.body.classList.remove('dragging');
      const finalSnapZone = currentSnapZoneRef.current;
      onDragEnd?.(position, finalSnapZone);
      dragStartRef.current = null;
      // Reset snap zone after drag ends
      currentSnapZoneRef.current = null;
      setSnapZone(null);
    }
  }, [onDragEnd, position]);

  // Add global listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      document.addEventListener('touchcancel', handleEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEnd);
        document.removeEventListener('touchcancel', handleEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleEnd]);

  const startDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (disabled) return;

      dragStartRef.current = {
        x: clientX,
        y: clientY,
        posX: position.x,
        posY: position.y,
      };

      setIsDragging(true);
      document.body.classList.add('dragging');
      onDragStart?.();
    },
    [disabled, position, onDragStart]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only left click
      if (e.button !== 0) return;
      e.preventDefault();
      startDrag(e.clientX, e.clientY);
    },
    [startDrag]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        startDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    [startDrag]
  );

  return {
    position,
    isDragging,
    snapZone,
    dragHandleProps: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart,
    },
    setPosition,
  };
};
